/* eslint-disable react/require-default-props */
import React from 'react';
import { Text } from '../../../atoms/Text';
import { ControlGroupItemComponent } from './components/ControlGroupItem';
import './ControlGroupStyles.scss';
/**
 * ControlGroup Component
 * Structural and functional wrapper for grouping related form controls
 * (checkboxes and/or radio buttons) under a shared context.
 * @version 1.0.0
 * @since 2026-03-09
 */
export function ControlGroup({ controlType, childrenControlType, items, value, onChange, label, disabled = false, className = '', id = 'control-group', name, error }) {
    const radioName = name || `${id}-radio`;
    // --- Root-level handlers ---
    const handleRootCheckboxToggle = (item, checked) => {
        if (item.children && item.children.length > 0) {
            // Tiered checkbox: parent click toggles all enabled children
            handleParentCheckboxChange(item);
        }
        else {
            // Base checkbox: toggle individual root value
            const newRoot = checked ? [...value.root, item.value] : value.root.filter((v) => v !== item.value);
            onChange(Object.assign(Object.assign({}, value), { root: newRoot }));
        }
    };
    const handleRootRadioSelect = (item) => {
        onChange(Object.assign(Object.assign({}, value), { root: [item.value] }));
    };
    // --- Parent checkbox + checkbox children logic ---
    const handleParentCheckboxChange = (item) => {
        const children = item.children || [];
        const enabledChildren = children.filter((child) => !child.disabled);
        const selectedChildren = value.children[item.value] || [];
        const enabledCheckedCount = enabledChildren.filter((child) => selectedChildren.includes(child.value)).length;
        const isAllChecked = enabledCheckedCount === enabledChildren.length;
        if (isAllChecked || enabledCheckedCount > 0) {
            // Uncheck all enabled children, keep disabled selections
            const disabledSelected = selectedChildren.filter((v) => { var _a; return (_a = children.find((child) => child.value === v)) === null || _a === void 0 ? void 0 : _a.disabled; });
            updateChildren(item.value, disabledSelected);
        }
        else {
            // Check all enabled children
            const enabledValues = enabledChildren.map((child) => child.value);
            const disabledSelected = selectedChildren.filter((v) => { var _a; return (_a = children.find((child) => child.value === v)) === null || _a === void 0 ? void 0 : _a.disabled; });
            updateChildren(item.value, [...disabledSelected, ...enabledValues]);
        }
    };
    // --- Children handlers ---
    const handleChildCheckboxToggle = (parentValue, toggledChildValue) => {
        const currentChildren = value.children[parentValue] || [];
        const newChildren = currentChildren.includes(toggledChildValue)
            ? currentChildren.filter((v) => v !== toggledChildValue)
            : [...currentChildren, toggledChildValue];
        updateChildren(parentValue, newChildren);
    };
    const handleChildRadioChange = (parentValue, selectedChildValue) => {
        updateChildren(parentValue, [selectedChildValue]);
    };
    const updateChildren = (parentValue, newChildrenValues) => {
        onChange(Object.assign(Object.assign({}, value), { children: Object.assign(Object.assign({}, value.children), { [parentValue]: newChildrenValues }) }));
    };
    // --- Derived state helpers ---
    const getIsRootSelected = (item) => {
        if (controlType === 'radio') {
            return value.root.includes(item.value);
        }
        // Checkbox root
        if (item.children && item.children.length > 0) {
            // Derived from children: parent is "checked" when ALL enabled children are checked
            const enabledChildren = item.children.filter((child) => !child.disabled);
            const selectedChildren = value.children[item.value] || [];
            const enabledCheckedCount = enabledChildren.filter((child) => selectedChildren.includes(child.value)).length;
            return enabledCheckedCount === enabledChildren.length && enabledChildren.length > 0;
        }
        return value.root.includes(item.value);
    };
    const getIsIndeterminate = (item) => {
        if (controlType !== 'checkbox' || !item.children || item.children.length === 0) {
            return false;
        }
        const enabledChildren = item.children.filter((child) => !child.disabled);
        const selectedChildren = value.children[item.value] || [];
        const enabledCheckedCount = enabledChildren.filter((child) => selectedChildren.includes(child.value)).length;
        const isAllChecked = enabledCheckedCount === enabledChildren.length && enabledChildren.length > 0;
        const isNoneChecked = enabledCheckedCount === 0;
        return !isAllChecked && !isNoneChecked;
    };
    return (React.createElement("div", { className: `qmb-ui-control-group ${className}` },
        React.createElement("fieldset", { className: "qmb-ui-control-group__fieldset" },
            label && (React.createElement("legend", { className: "qmb-ui-control-group__legend" },
                React.createElement(Text, { variant: "label" }, label))),
            React.createElement("div", { className: "qmb-ui-control-group__items", role: controlType === 'radio' ? 'radiogroup' : 'group' }, items.map((item) => (React.createElement(ControlGroupItemComponent, { key: item.value, item: item, controlType: controlType, childrenControlType: childrenControlType, isSelected: getIsRootSelected(item), isIndeterminate: getIsIndeterminate(item), onCheckboxChange: (checked) => handleRootCheckboxToggle(item, checked), onRadioChange: () => handleRootRadioSelect(item), selectedChildrenValues: value.children[item.value] || [], onChildCheckboxToggle: (childValue) => handleChildCheckboxToggle(item.value, childValue), onChildRadioChange: (childValue) => handleChildRadioChange(item.value, childValue), disabled: disabled, radioName: radioName, idPrefix: id }))))),
        error && React.createElement("span", { className: "qmb-ui-control-group__error" }, error)));
}
